import { CommonModule } from '@angular/common';
import { Component, ElementRef, QueryList, ViewChildren, ChangeDetectorRef, inject, signal } from '@angular/core';
import { SidebarService } from '../../services/sidebar.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { SafeHtmlPipe } from '../../pipe/safe-html.pipe';
import { TPipe } from '../../pipe/t.pipe';
import { LanguageSwitchComponent } from '../../components/common/language-switch/language-switch.component';
import { AuthService } from '../../services/auth.service';
import { OperatorsService } from '../../services/operators.service';
import { combineLatest, Subscription } from 'rxjs';

type NavItem = {
  name: string;
  icon: string;
  path?: string;
  new?: boolean;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterModule,
    SafeHtmlPipe,
    TPipe,
    LanguageSwitchComponent,
  ],
  templateUrl: './app-sidebar.component.html',
})
export class AppSidebarComponent {

  private readonly auth = inject(AuthService);
  private readonly operators = inject(OperatorsService);

  /** Nom commercial de l'opérateur pour la carte identité (chargé 1× au boot). */
  private readonly _businessName = signal<string | null>(null);

  /** businessName si présent, sinon le nom de la personne connectée. */
  get workspaceName(): string {
    const u = this.auth.user();
    return this._businessName() || u?.displayName || u?.username || '—';
  }

  /** Initiale pour l'avatar carré de la carte identité (mode réduit compris). */
  get workspaceInitial(): string {
    const n = this.workspaceName.trim();
    return n && n !== '—' ? n.charAt(0).toUpperCase() : '·';
  }

  // ── Icônes SVG réutilisées ────────────────────────────────────────────
  private static readonly ICON_DASHBOARD = `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M5.5 3.25C4.25736 3.25 3.25 4.25736 3.25 5.5V8.99998C3.25 10.2426 4.25736 11.25 5.5 11.25H9C10.2426 11.25 11.25 10.2426 11.25 8.99998V5.5C11.25 4.25736 10.2426 3.25 9 3.25H5.5ZM4.75 5.5C4.75 5.08579 5.08579 4.75 5.5 4.75H9C9.41421 4.75 9.75 5.08579 9.75 5.5V8.99998C9.75 9.41419 9.41421 9.74998 9 9.74998H5.5C5.08579 9.74998 4.75 9.41419 4.75 8.99998V5.5ZM5.5 12.75C4.25736 12.75 3.25 13.7574 3.25 15V18.5C3.25 19.7426 4.25736 20.75 5.5 20.75H9C10.2426 20.75 11.25 19.7427 11.25 18.5V15C11.25 13.7574 10.2426 12.75 9 12.75H5.5ZM4.75 15C4.75 14.5858 5.08579 14.25 5.5 14.25H9C9.41421 14.25 9.75 14.5858 9.75 15V18.5C9.75 18.9142 9.41421 19.25 9 19.25H5.5C5.08579 19.25 4.75 18.9142 4.75 18.5V15ZM12.75 5.5C12.75 4.25736 13.7574 3.25 15 3.25H18.5C19.7426 3.25 20.75 4.25736 20.75 5.5V8.99998C20.75 10.2426 19.7426 11.25 18.5 11.25H15C13.7574 11.25 12.75 10.2426 12.75 8.99998V5.5ZM15 4.75C14.5858 4.75 14.25 5.08579 14.25 5.5V8.99998C14.25 9.41419 14.5858 9.74998 15 9.74998H18.5C18.9142 9.74998 19.25 9.41419 19.25 8.99998V5.5C19.25 5.08579 18.9142 4.75 18.5 4.75H15ZM15 12.75C13.7574 12.75 12.75 13.7574 12.75 15V18.5C12.75 19.7426 13.7574 20.75 15 20.75H18.5C19.7426 20.75 20.75 19.7427 20.75 18.5V15C20.75 13.7574 19.7426 12.75 18.5 12.75H15ZM14.25 15C14.25 14.5858 14.5858 14.25 15 14.25H18.5C18.9142 14.25 19.25 14.5858 19.25 15V18.5C19.25 18.9142 18.9142 19.25 18.5 19.25H15C14.5858 19.25 14.25 18.9142 14.25 18.5V15Z" fill="currentColor"></path></svg>`;
  private static readonly ICON_USERS = `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 14.1526 4.3002 16.1184 5.61936 17.616C6.17279 15.3096 8.24852 13.5955 10.7246 13.5955H13.2746C15.7509 13.5955 17.8268 15.31 18.38 17.6167C19.6996 16.119 20.5 14.153 20.5 12C20.5 7.30558 16.6944 3.5 12 3.5ZM17.0246 18.8566V18.8455C17.0246 16.7744 15.3457 15.0955 13.2746 15.0955H10.7246C8.65354 15.0955 6.97461 16.7744 6.97461 18.8455V18.856C8.38223 19.8895 10.1198 20.5 12 20.5C13.8798 20.5 15.6171 19.8898 17.0246 18.8566ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM11.9991 7.25C10.8847 7.25 9.98126 8.15342 9.98126 9.26784C9.98126 10.3823 10.8847 11.2857 11.9991 11.2857C13.1135 11.2857 14.0169 10.3823 14.0169 9.26784C14.0169 8.15342 13.1135 7.25 11.9991 7.25ZM8.48126 9.26784C8.48126 7.32499 10.0563 5.75 11.9991 5.75C13.9419 5.75 15.5169 7.32499 15.5169 9.26784C15.5169 11.2107 13.9419 12.7857 11.9991 12.7857C10.0563 12.7857 8.48126 11.2107 8.48126 9.26784Z" fill="currentColor"></path></svg>`;
  private static readonly ICON_BELL = `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.5 9.5a5.5 5.5 0 0 1 11 0c0 4 1.5 5.5 1.5 5.5H5s1.5-1.5 1.5-5.5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M10 18.5a2 2 0 0 0 4 0" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;

  private static readonly ICON_WALLET = `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M5.25 5.5A2.75 2.75 0 0 0 2.5 8.25v7.5A2.75 2.75 0 0 0 5.25 18.5h13.5a2.75 2.75 0 0 0 2.75-2.75v-7.5A2.75 2.75 0 0 0 18.75 5.5H5.25ZM4 8.25c0-.69.56-1.25 1.25-1.25h13.5c.69 0 1.25.56 1.25 1.25v.75h-4.25a3 3 0 0 0 0 6H20v.75c0 .69-.56 1.25-1.25 1.25H5.25c-.69 0-1.25-.56-1.25-1.25v-7.5Zm16 2.25v3h-4.25a1.5 1.5 0 0 1 0-3H20Z" fill="currentColor"></path></svg>`;
  private static readonly ICON_LICENSE = `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M6.25 3.5A2.75 2.75 0 0 0 3.5 6.25v11.5A2.75 2.75 0 0 0 6.25 20.5h11.5a2.75 2.75 0 0 0 2.75-2.75V6.25A2.75 2.75 0 0 0 17.75 3.5H6.25ZM5 6.25C5 5.56 5.56 5 6.25 5h11.5c.69 0 1.25.56 1.25 1.25v11.5c0 .69-.56 1.25-1.25 1.25H6.25C5.56 19 5 18.44 5 17.75V6.25ZM7.5 8.5a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 8.5Zm0 3.5a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 7.5 12Zm.75 2.75a.75.75 0 0 0 0 1.5h4a.75.75 0 0 0 0-1.5h-4Z" fill="currentColor"></path></svg>`;
  private static readonly ICON_SHIELD = `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.4 2.6a1.5 1.5 0 0 1 1.2 0l6.5 2.79c.55.24.9.78.9 1.38v5.02c0 3.77-2.3 7.16-5.8 8.57l-1.66.67a1.5 1.5 0 0 1-1.12 0l-1.66-.67A9.25 9.25 0 0 1 3.96 11.8V6.77c0-.6.35-1.14.9-1.38L11.4 2.6Zm.6 1.48L5.96 6.66a.25.25 0 0 0-.15.23v4.91c0 3.16 1.93 6 4.86 7.18l1.33.54 1.33-.54a7.75 7.75 0 0 0 4.86-7.18V6.89a.25.25 0 0 0-.15-.23L12 4.08Zm3.36 4.4a.75.75 0 0 1 .1 1.06l-3.6 4.33a.75.75 0 0 1-1.1.05l-1.93-1.93a.75.75 0 1 1 1.06-1.06l1.35 1.34 3.06-3.68a.75.75 0 0 1 1.06-.1Z" fill="currentColor"></path></svg>`;
  private static readonly ICON_CASHOUT = `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 3v10m0 0 3.5-3.5M12 13l-3.5-3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
  private static readonly ICON_BALANCE = `<svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 4v16M7 20h10M12 6.5 5 9m7-2.5L19 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M2.5 13.5a2.5 2.5 0 0 0 5 0L5 9l-2.5 4.5ZM16.5 13.5a2.5 2.5 0 0 0 5 0L19 9l-2.5 4.5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>`;

  // ── Navigation principale (modules operator Nawam) ────────────────────
  // NOTE : shell en construction. Les entrées operator restantes (commissions,
  // kyc, licence/zone) sont ajoutées au fil des modules livrés.
  // NOTE i18n : `name` porte une **clé** de traduction (cf. shared/i18n/messages).
  // Le template rend `{{ nav.name | t }}` — bascule FR/EN instantanée.
  navItems: NavItem[] = [
    {
      icon: AppSidebarComponent.ICON_DASHBOARD,
      name: 'nav.dashboard',
      path: '/',
    },
    {
      icon: AppSidebarComponent.ICON_USERS,
      name: 'nav.prestataires',
      path: '/prestataires',
    },
    {
      icon: AppSidebarComponent.ICON_WALLET,
      name: 'nav.wallet',
      path: '/wallet',
    },
    {
      icon: AppSidebarComponent.ICON_CASHOUT,
      name: 'nav.withdrawals',
      path: '/withdrawals',
    },
    {
      icon: AppSidebarComponent.ICON_LICENSE,
      name: 'nav.license',
      path: '/license',
    },
    {
      icon: AppSidebarComponent.ICON_BALANCE,
      name: 'nav.reconciliation',
      path: '/cash-reconciliation',
    },
    {
      icon: AppSidebarComponent.ICON_SHIELD,
      name: 'nav.kyc',
      path: '/kyc',
    },
    {
      icon: AppSidebarComponent.ICON_BELL,
      name: 'nav.notifications',
      path: '/notifications',
    },
  ];

  /** Pas de groupe « Démo template » côté operator — UI épurée (comme owner). */
  othersItems: NavItem[] = [];

  openSubmenu: string | null | number = null;
  subMenuHeights: { [key: string]: number } = {};
  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  readonly isExpanded$;
  readonly isMobileOpen$;
  readonly isHovered$;

  private subscription: Subscription = new Subscription();

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.isHovered$ = this.sidebarService.isHovered$;
  }

  ngOnInit() {
    // Subscribe to router events
    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(this.router.url);
        }
      })
    );

    // Subscribe to combined observables to close submenus when all are false
    this.subscription.add(
      combineLatest([this.isExpanded$, this.isMobileOpen$, this.isHovered$]).subscribe(
        ([isExpanded, isMobileOpen, isHovered]) => {
          if (!isExpanded && !isMobileOpen && !isHovered) {
            // this.openSubmenu = null;
            // this.savedSubMenuHeights = { ...this.subMenuHeights };
            // this.subMenuHeights = {};
            this.cdr.detectChanges();
          } else {
            // Restore saved heights when reopening
            // this.subMenuHeights = { ...this.savedSubMenuHeights };
            // this.cdr.detectChanges();
          }
        }
      )
    );

    // Initial load
    this.setActiveMenuFromRoute(this.router.url);

    // Nom commercial de l'opérateur pour la carte identité (best-effort, 1 appel).
    this.subscription.add(
      this.operators.myDashboard().subscribe({
        next: (d) => this._businessName.set(d?.businessName ?? null),
        error: () => {
          /* pas d'opérateur / non provisionné → repli sur le nom de la personne */
        },
      }),
    );
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.subscription.unsubscribe();
  }

  isActive(path: string): boolean {
    return this.router.url === path;
  }

  toggleSubmenu(section: string, index: number) {
    const key = `${section}-${index}`;

    if (this.openSubmenu === key) {
      this.openSubmenu = null;
      this.subMenuHeights[key] = 0;
    } else {
      this.openSubmenu = key;

      setTimeout(() => {
        const el = document.getElementById(key);
        if (el) {
          this.subMenuHeights[key] = el.scrollHeight;
          this.cdr.detectChanges(); // Ensure UI updates
        }
      });
    }
  }

  onSidebarMouseEnter() {
    this.isExpanded$.subscribe(expanded => {
      if (!expanded) {
        this.sidebarService.setHovered(true);
      }
    }).unsubscribe();
  }

  private setActiveMenuFromRoute(currentUrl: string) {
    const menuGroups = [
      { items: this.navItems, prefix: 'main' },
      { items: this.othersItems, prefix: 'others' },
    ];

    menuGroups.forEach(group => {
      group.items.forEach((nav, i) => {
        if (nav.subItems) {
          nav.subItems.forEach(subItem => {
            if (currentUrl === subItem.path) {
              const key = `${group.prefix}-${i}`;
              this.openSubmenu = key;

              setTimeout(() => {
                const el = document.getElementById(key);
                if (el) {
                  this.subMenuHeights[key] = el.scrollHeight;
                  this.cdr.detectChanges(); // Ensure UI updates
                }
              });
            }
          });
        }
      });
    });
  }

  onSubmenuClick() {
    console.log('click submenu');
    this.isMobileOpen$.subscribe(isMobile => {
      if (isMobile) {
        this.sidebarService.setMobileOpen(false);
      }
    }).unsubscribe();
  }  

  
}
